import { TRIG_FUNCTION_KEYS } from "./mappings";

import type { TrigFlags } from "./buildSympyCode";
import type { NodeJson } from "@corca/data";

/** A subtree of pure arithmetic — no variable, constant, unit or matrix anywhere in it. */
function isBareNumericSubtree(node: NodeJson): boolean {
  if (["variable", "constant", "unit", "matrix"].includes(node.type)) return false;
  return node.children.every(isBareNumericSubtree);
}

/**
 * Inspect the expression tree to determine how trig functions should be evaluated:
 * - hasBareNumericTrigArg: any trig function whose argument subtree contains only numbers
 *   (no variables, constants, or units) → caller will evaluate numerically via sp.N(expr)
 *
 * Degree (°) units are handled separately in buildSympyCode, which detects the emitted
 * `units.degree` literal in the expression string and substitutes it with π/180.
 */
export function detectTrigFlags(nodes: NodeJson[]): TrigFlags {
  let hasBareNumericTrigArg = false;

  const visit = (node: NodeJson): void => {
    if (node.type === "function" && TRIG_FUNCTION_KEYS.has(node.props.key ?? "")) {
      const args = node.children.filter((child) => child.type === "inline");
      if (args.length > 0 && args.every(isBareNumericSubtree)) {
        hasBareNumericTrigArg = true;
      }
    }
    for (const child of node.children) visit(child);
  };

  for (const root of nodes) visit(root);
  return { hasBareNumericTrigArg };
}
